/**
 * @flow
 */

import "./stylesheet.css"

import { TweenLite } from "gsap"
import _ from "lodash"
import React, { useCallback, useContext, useEffect, useRef } from "react"
import type { Node } from "react"
// import { isBrowser } from "react-device-detect"
import styled from "styled-components"

import Cursor from "../Cursor"
import { ScrollerContext } from "./context"

type Props = {
  children: Node,
}

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  overflow: hidden;
`

const Content = styled.div`
  background-color: cream;
`

const Scrollbar = styled.div`
  display: none;
  @media (min-width: 768px) {
    display: block;
    position: absolute;
    top: 0;
    right: 0;
    width: 5px;
    height: 100vh;
    /* background-color: rgba(0, 0, 0, 0.25); */
    .progress {
      position: absolute;
      background-color: rgba(0, 0, 0, 0.95);
      width: 100%;
      height: 100%;
      bottom: 100%;
      left: 0;
    }
  }
`

const SmoothScroll = ({ children, ...props }: Props) => {
  const context = useContext(ScrollerContext)

  const scrollEl = useRef(null)
  const scrollContentEl = useRef(null)
  const scrollBar = useRef(null)

  const frameId = useRef(null)
  const scroller = useRef({})

  const setHeight = () => {
    if (document.body && scrollContentEl.current) {
      document.body.style.height = `${scrollContentEl.current.offsetHeight}px`
    }
  }

  const setScroller = () => {
    scroller.current = {
      target: scrollContentEl.current,
      ease: 0.1,
      endY: 0,
      y: 0,
      resizeRequest: 1,
      scrollRequest: 0,
    }

    if (scroller.current.target) {
      TweenLite.set(scroller.current.target, {
        rotation: 0.01,
        force3D: true,
      })
    }
  }

  const { toggleScrolling } = context

  const updateScroller = useCallback(() => {
    const resized = scroller.current.resizeRequest > 0

    if (resized) {
      setHeight()
      scroller.current.resizeRequest = 0
    }

    let scrollY = window.pageYOffset

    // scrollBar animation
    let pageHeight = _.get(document, "body").getBoundingClientRect().height
    let percentage = scrollY / (pageHeight - window.innerHeight)
    let scrollPercentage = percentage * 100
    TweenLite.set(scrollBar.current, { y: `${scrollPercentage}%` })
    // eo scrollBar animation

    scroller.current.endY = scrollY
    scroller.current.y += (scrollY - scroller.current.y) * scroller.current.ease

    if (Math.abs(scrollY - scroller.current.y) < 0.05) {
      scroller.current.y = scrollY
      scroller.current.scrollRequest = 0
      // eslint-disable-next-line no-console
      console.log("done scrolling")
      toggleScrolling()
      // set scrolling state to zero
    }

    TweenLite.set(scroller.current.target, {
      y: -scroller.current.y,
    })

    frameId.current =
      scroller.current.scrollRequest > 0
        ? window.requestAnimationFrame(updateScroller)
        : null
  }, [toggleScrolling])

  /* Listener events */
  const onScroll = useCallback(() => {
    if (scroller.current) {
      scroller.current.scrollRequest++
    }
    if (!frameId.current) {
      // eslint-disable-next-line no-console
      console.log("start scrolling")
      toggleScrolling()
      // start scrolling
      frameId.current = window.requestAnimationFrame(updateScroller)
    }
  }, [toggleScrolling, updateScroller])

  const onResize = useCallback(() => {
    if (scroller.current) {
      scroller.current.resizeRequest++
    }
    if (!frameId.current) {
      frameId.current = window.requestAnimationFrame(updateScroller)
    }
  }, [updateScroller])

  useEffect(() => {
    if (context.isActive) {
      setHeight()
      setScroller()
      window.addEventListener("scroll", onScroll, { passive: true })
      window.addEventListener("resize", onResize, { passive: true })
    } else {
      window.removeEventListener("scroll", onScroll, { passive: true })
      window.removeEventListener("resize", onResize, { passive: true })
    }

    return () => {
      window.removeEventListener("scroll", onScroll, { passive: true })
      window.removeEventListener("resize", onResize, { passive: true })
      window.cancelAnimationFrame(frameId.current) // remove current if this breaks anything
    }
  }, [context.isActive, onScroll, onResize])

  return (
    <Container ref={scrollEl} {...props}>
      <Content ref={scrollContentEl}>{children}</Content>
      <Scrollbar>
        <div className="progress" ref={scrollBar} />
      </Scrollbar>
      <Cursor />
    </Container>
  )
}

export default SmoothScroll

/*

scrolling starts at zero
scrolling / doc height

make the height of the after element the same as the height of the document
translate the after element the same distance of the 
scrollY / doc height * 100% for the transform translate

the use effect is working nicely for now
but perhaps just have another look at it and how it is getting triggerd
when the intro animation is finished and its ready to scroll for the first time
and perhaps it should also be triggered abit sooner like on start of a certain animation piece towards the end

understand the lerp function
see if it can be made universal

as everything animation based needs to be done inside the updateScroller

useLerp

seperate the scrolling event from the lerping event
one scroller with multple lerpers

just know that element might be in




README

Overview:
Wrap the application with a fixed container with overflow hidden
Animate the inner content of the container with a transform function
Ease this function with GSAP

  - Writing your own lerp function can replace the need for GSAP but that can be next steps

setHeight: function
  used to set the body height based on the height of the inner content

setScroller: function
  sets the initial scroller variable

onScroll: function
  update the current scroll position

updateScroller: function
  animate the inner content position based on where the scroll position of the user stopped over an ease function

*/
